import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { DECISION_KINDS, STAGES, STAGE_IDS } from "@/content/journey";
import { ExplorerFrame, Swap } from "@/components/site/ExplorerFrame";
import { Mono } from "@/components/site/primitives";
import { cn } from "@/lib/utils";
import { StageRail, StageStepper } from "./StageRail";

const DecisionBadge = ({ kind }) => (
  <span className={cn("mt-2 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.14em]", DECISION_KINDS[kind].tone)}>
    <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current" />
    {DECISION_KINDS[kind].label}
  </span>
);

const Cell = ({ term, className, children }) => (
  <div className={cn("border-o-line p-5 sm:p-6", className)}>
    <Mono>{term}</Mono>
    <p className="mt-3 leading-relaxed">{children}</p>
  </div>
);

const StageDetail = ({ st }) => (
  <div className="grid md:grid-cols-2" data-testid="journey-detail">
    <Cell term="What you experience" className="border-b md:border-r">{st.experiences}</Cell>
    <Cell term="What Ordo Intelligence carries" className="border-b">{st.carries}</Cell>
    <Cell term="What you receive" className="border-b md:border-b-0 md:border-r">{st.receives}</Cell>
    <div className="bg-o-raise p-5 sm:p-6" data-testid="journey-decision">
      <Mono>Is a new decision involved?</Mono>
      <div><DecisionBadge kind={st.decision.kind} /></div>
      <p className="mt-3 leading-relaxed">{st.decision.text}</p>
      {st.exit && (
        <p className="mt-4 border-l-2 border-o-accent pl-3 text-sm" data-testid="journey-exit-note">
          Exit point: you may choose not to proceed beyond this stage.
        </p>
      )}
    </div>
  </div>
);

const JourneyText = () => (
  <ol className="divide-y divide-o-line" data-testid="journey-text">
    {STAGES.map((s) => (
      <li key={s.id} className="grid gap-4 p-5 sm:p-7 lg:grid-cols-12">
        <div className="lg:col-span-3">
          <Mono accent>{s.index}</Mono>
          <h3 className="mt-2 font-display text-xl font-bold">{s.label}</h3>
          <DecisionBadge kind={s.decision.kind} />
        </div>
        <dl className="grid gap-4 text-sm sm:grid-cols-2 lg:col-span-9">
          <div><dt><Mono>You experience</Mono></dt><dd className="mt-1">{s.experiences}</dd></div>
          <div><dt><Mono>We carry</Mono></dt><dd className="mt-1">{s.carries}</dd></div>
          <div><dt><Mono>You receive</Mono></dt><dd className="mt-1">{s.receives}</dd></div>
          <div><dt><Mono>Decision</Mono></dt><dd className="mt-1">{s.decision.text}</dd></div>
        </dl>
      </li>
    ))}
  </ol>
);

export const JourneyExplorer = () => {
  const [params] = useSearchParams();
  const initial = STAGE_IDS.includes(params.get("stage")) ? params.get("stage") : "understand";
  const [sel, setSel] = useState(initial);
  const [mode, setMode] = useState("explore");
  const st = STAGES[STAGE_IDS.indexOf(sel)];
  return (
    <ExplorerFrame
      testid="journey-explorer"
      label="Customer journey explorer"
      hint="Select a stage to see what you experience, what we carry, what you receive, and whether a new decision is involved."
      mode={mode}
      onMode={setMode}
    >
      {mode === "explore" ? (
        <>
          <StageRail stages={STAGES} sel={sel} onSelect={setSel} testid="journey" renderMeta={(s) => <DecisionBadge kind={s.decision.kind} />} />
          <div aria-live="polite">
            <Swap k={sel}><StageDetail st={st} /></Swap>
          </div>
          <StageStepper ids={STAGE_IDS} sel={sel} onSelect={setSel} testid="journey" />
        </>
      ) : (
        <JourneyText />
      )}
    </ExplorerFrame>
  );
};
